<script lang="ts" setup>
import type { Action } from '@thecompaniesapi/shared/client'

const props = defineProps({
  action: {
    required: true,
    type: Object as PropType<Action>,
  },
  list: {
    required: true,
    type: Object as PropType<List>,
  },
})

const { t } = useI18n()
const actionsStore = useActionsStore()

const statusColor = computed(() => {
  if (['active', 'pending'].includes(props.action.status)) {
    return 'blue'
  }
  if (props.action.status === 'failed') {
    return 'red'
  }
  if (props.action.status === 'completed') {
    return 'green'
  }

  return 'yellow'
})

const isPinging = computed(() => {
  return ['active', 'pending'].includes(props.action.status)
})

// Methods
function onClose() {
  actionsStore.selectedActionId = undefined
}
</script>

<template>
  <div class="flex items-center gap-2">
    <UTooltip
      :popper="{ placement: 'bottom' }"
      :text="t(`actions.jobs.${action?.status}.${action?.data?.job}`, { list: list?.name })"
    >
      <AnimatedBorder
        :duration="isPinging ? 2000 : 6000"
        :hoverable="false"
        :opacity="isPinging ? 0.8 : 0"
        :spread="isPinging ? 8 : 0"
      >
        <UButton
          class="gradient-texture-reverse pointer-events-none flex items-center gap-2 text-xs uppercase"
          size="sm"
        >
          <div class="flex items-center gap-2">
            <div class="flex items-center gap-2">
              <BasePingDot
                class="hidden sm:inline-block"
                :color="statusColor"
                :pinging="isPinging"
              />

              <UBadge
                v-if="action?.data?.job"
                class="truncate"
                color="blue"
                :label="action.data.job"
                size="xs"
                variant="subtle"
              />
            </div>

            <template v-if="!action.data?.totalDomains">
              {{ t('noCompanies') }}
            </template>

            <template v-else>
              <template v-if="action.data?.totalDomains > 0">
                <span class="flex items-center gap-1 text-green-500 dark:text-green-400">
                  <UIcon
                    class="size-4"
                    :name="getIcon('check')"
                  />
                  {{ numbro(action.data?.completedDomains || 0) }}
                </span>

                <span
                  v-if="action.data?.failedDomains && action.data.failedDomains > 0"
                  class="flex items-center gap-1 text-red-500 dark:text-red-400"
                >
                  <UIcon
                    class="size-4"
                    :name="getIcon('error')"
                  />
                  {{ numbro(action.data?.failedDomains || 0) }}
                </span>
              </template>
            </template>
          </div>
        </UButton>
      </AnimatedBorder>
    </UTooltip>

    <UTooltip
      :popper="{ placement: 'bottom' }"
      :text="action?.listId ? t('closeToList') : t('closeToSearch')"
    >
      <UButton
        :icon="getIcon('close')"
        size="md"
        @click="onClose"
      />
    </UTooltip>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "closeToList": "Get back to the complete list",
    "closeToSearch": "Get back to the search",
    "completed": "Completed",
    "noCompanies": "No companies"
  },
  "es": {
    "closeToList": "Volver a la lista completa",
    "closeToSearch": "Volver a la búsqueda",
    "completed": "Completado",
    "noCompanies": "No hay empresas"
  },
  "fr": {
    "closeToList": "Retour à la liste complète",
    "closeToSearch": "Retour à la recherche",
    "completed": "Terminé",
    "noCompanies": "Aucune entreprise"
  }
}
</i18n>
